import { Injectable } from '@angular/core';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { UtcDatePipe } from '@epicuro-next/ataraxia/date';
import { ListProductReference } from '@epicuro-next/data/lists';
import { plural } from '@epicuro-next/platform/i18n';
import {
  DocumentControlRuleType,
  selectIsValidatorAllowedByRule,
} from '@epicuro-next/state/document-control-state';
import {
  applyCommercialListControl,
  isMultiplierAction,
  selectListEntityFactory,
} from '@epicuro-next/state/list-entities';
import { selectCommercialListProductReferencesFactory } from '@epicuro-next/state/list-product-reference-entities';
import {
  selectCommercialListMultiplierEnabled,
  selectCommercialProductListConsumptionEnabled,
} from '@epicuro-next/state/product-list-state';
import {
  selectIsUpdatingBasketForCommercialList,
  shopTheCommercialList,
} from '@epicuro-next/state/ubc-communication/commercial-list';
import { UBCMessageName } from '@epicuro-next/state/ubc-communication/core';
import { isNonNullish, stringDaysEqual } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { List } from '@epicuro-next/models/engagement';
import { DateTime } from 'luxon';
import { combineLatest, iif, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

interface State {
  list?: List;
}

@Injectable()
export class CommercialListStore extends ComponentStore<State> {
  constructor(private store: Store, private utcPipe: UtcDatePipe) {
    super();
    this.setState({});
    this.clearListMultiplierOnNewLists$();
  }

  public list$ = this.select((s) => s.list);
  public listId$ = this.list$.pipe(map((list) => list?.id));

  public productReferenceEntity$ = this.listId$.pipe(
    switchMap((listId) =>
      !!listId
        ? this.store.pipe(
            select(selectCommercialListProductReferencesFactory(listId)),
          )
        : of(undefined),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public deliveryInfoLoading$ = this.productReferenceEntity$.pipe(
    map((entity) => entity?.loading),
  );

  public deliveryInfoError$ = this.productReferenceEntity$.pipe(
    map((entity) => entity?.error),
  );

  public productReferenceData$ = this.productReferenceEntity$.pipe(
    map((entity) => entity?.data),
  );

  public hasSizeMissedPrice$ = this.select(
    this.productReferenceData$,
    (data) =>
      !!data?.productReferences.some((productRef) =>
        productRef.deliveryInfo.some(({ price }) => !price),
      ),
  );

  public deliveryDates$ = this.productReferenceData$.pipe(
    map((data) => {
      if (!data) return undefined;
      const { fromDeliveryDate, toDeliveryDate } = data.availableItemTotals;

      if (stringDaysEqual(fromDeliveryDate, toDeliveryDate)) {
        return {
          date: fromDeliveryDate,
        };
      }
      return {
        from: fromDeliveryDate,
        to: toDeliveryDate,
        text: this.readableDateRange(fromDeliveryDate, toDeliveryDate),
      };
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public showCommercialListLoader$ = this.store.pipe(
    select(selectIsUpdatingBasketForCommercialList),
  );

  public commercialListConsumptionEnabled$ = this.store.pipe(
    select(selectCommercialProductListConsumptionEnabled),
  );

  public listEntity$ = this.listId$.pipe(
    filter(isNonNullish),
    distinctUntilChanged(),
    switchMap((id) => this.store.pipe(select(selectListEntityFactory(id)))),
  );

  public multiplierEnabled$ = combineLatest([
    atxIsMobile$,
    this.store.pipe(select(selectCommercialListMultiplierEnabled)),
  ]).pipe(map(([isMobile, enabled]) => !isMobile && enabled));

  public showCommercialListMultiplier$ = this.listId$.pipe(
    filter(isNonNullish),
    switchMap((id) =>
      this.store.pipe(
        select(
          selectIsValidatorAllowedByRule(
            id,
            DocumentControlRuleType.MultiplyNumberOfPackages,
          ),
        ),
      ),
    ),
    mergeMap((isEnabledInDocumentControl) =>
      iif(() => isEnabledInDocumentControl, of(true), this.multiplierEnabled$),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public multiplier$ = this.listEntity$.pipe(
    map((entity) => entity?.commercialListControlActions ?? []),
    map((actions) => actions.find(isMultiplierAction)),
    map((rule) => rule?.multiplyValue ?? 1),
  );

  public buttonText$ = this.multiplier$.pipe(
    map((multiplier) => {
      return plural(
        multiplier,
        {
          1: $localize`:commercialList|Add 1 to basket button text@@commercialListAdd1PackageToBasketButton: Add package to basket`,
        },
        $localize`:commercialList|Add many to basket button text@@commercialListAddManyPackagesToBasketButton:Add ${multiplier} packages to basket`,
      );
    }),
  );

  public disabled$ = combineLatest([
    this.deliveryInfoLoading$,
    this.productReferenceData$,
    this.commercialListConsumptionEnabled$,
  ]).pipe(
    map(([loading, data, enabled]) => {
      if (!enabled) {
        return $localize`:commercialList|disabled due to rule@@commercialListButtonDisabledByRuleMessage:Commercial lists are not supported`;
      }
      if (loading) {
        return $localize`:commercialList|disabled due to loading@@commercialListButtonDisabledByLoadingMessage:Commercial list data is loading`;
      }
      if (!data) {
        return $localize`:commercialList|disabled due to data@@commercialListButtonDisabledByDataMessage:Commercial list data is not valid`;
      }
      if (data.availableItemTotals.totalRequestedQuantity <= 0) {
        return $localize`:commercialList|disabled due to no products@@commercialListButtonDisabledByNoProductsMessage:No available products to add`;
      }
      return undefined;
    }),
  );

  public mixedDeliveryDates$ = this.deliveryDates$.pipe(
    map((dates) => (dates?.from && dates?.to ? dates : undefined)),
  );

  public singleDeliveryDate$ = this.deliveryDates$.pipe(
    map((dates) => (dates?.date ? dates.date : undefined)),
  );

  public clearListMultiplierOnNewLists$ = this.effect((void$) =>
    void$.pipe(
      switchMap(() =>
        this.list$.pipe(
          map((list) => list?.id),
          distinctUntilChanged(),
          filter(isNonNullish),
          takeUntil(this.destroy$),
          tapResponse(
            (listId) => this.multiply(1, listId),
            () => {
              /* noop */
            },
          ),
        ),
      ),
    ),
  );

  public setList(list: List) {
    this.patchState({ list });
  }

  public shopTheBasket(
    listId: string,
    productReferences: ListProductReference[],
  ) {
    this.store.dispatch(
      shopTheCommercialList({
        messageName: UBCMessageName.ShopTheCommercialList,
        payload: {
          listId,
          productReferences,
        },
      }),
    );
  }

  public multiply(value: number, listId: string) {
    this.store.dispatch(
      applyCommercialListControl({
        listId,
        controlAction: {
          type: 'multiplier',
          multiplyValue: value,
        },
      }),
    );
  }

  private readableDateRange(from: string, to: string) {
    const fromDate = DateTime.fromISO(from).toUTC();
    const toDate = DateTime.fromISO(to).toUTC();
    const t = (value: string, format?: string) =>
      this.utcPipe.transform(value, format);

    return fromDate.year === toDate.year
      ? `${t(from, 'MMM dd')} - ${t(to, 'MMM dd')}, ${t(to, 'yyyy')}`
      : `${t(from, 'MMM dd, yyyy')} - ${t(to, 'MMM dd, yyyy')}`;
  }
}
